import { useSuspenseQuery } from '@tanstack/react-query'

import { LexicalRichTextField } from '@/app/[locale]/price/_components/Richtext'
import { payloadCmsFetcher } from '@/libs/fetcher/presets'
import { getPayloadLocale } from '@/utils/campaign'

interface PayloadMediaField {
  id: string
  alt: string
  url: string
}

interface TradeDrawerContent {
  campaignMessage: LexicalRichTextField
  defaultIcon: PayloadMediaField
  icon?: PayloadMediaField
}

interface CampaignBannerContent {
  campaignTitle: string
  campaignDescription: string
  campaignImage: PayloadMediaField
  ctaLabel: string
  ctaLink: string
}

export interface CmsCampaign {
  id: string
  coinMode: 'all' | 'specific' | 'all_except'
  coins?: string | null
  tradeDrawer?: TradeDrawerContent
  campaignBanner?: CampaignBannerContent
  restrictedIps?: string
  allowedIps?: string
  noExpiryDate?: boolean
  campaignEndDate?: string
}

const fetchTradeDrawer = async (locale: string): Promise<CmsCampaign | null> => {
  const payloadLocale = getPayloadLocale(locale)
  const list = await payloadCmsFetcher<{ docs: Array<{ id: string }> }>(
    `/price-page-campaigns?placement=trade_drawer`,
  )
  const id = list?.docs?.[0]?.id
  if (!id) return null
  return payloadCmsFetcher<CmsCampaign>(`/price-page-campaigns/${id}?locale=${payloadLocale}`)
}

export const fetchCmsCampaigns = async (locale: string): Promise<CmsCampaign[]> => {
  const payloadLocale = getPayloadLocale(locale)
  const list = await payloadCmsFetcher<{ docs: Array<{ id: string }> }>(
    `/price-page-campaigns?placement=campaign_banner`,
  )
  const ids = list?.docs?.map(d => d.id) ?? []
  if (!ids.length) return []
  const results = await Promise.all(
    ids.map(id =>
      payloadCmsFetcher<CmsCampaign>(`/price-page-campaigns/${id}?locale=${payloadLocale}`),
    ),
  )
  return results as CmsCampaign[]
}

export const useTradeDrawer = (locale: string) =>
  useSuspenseQuery({
    queryKey: ['tradeDrawer', locale],
    queryFn: () => fetchTradeDrawer(locale),
  })

export const useCmsCampaigns = (locale: string) =>
  useSuspenseQuery({
    queryKey: ['cmsCampaigns', locale],
    queryFn: () => fetchCmsCampaigns(locale),
  })
